import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { StarIcon, MessageSquareIcon, UserCheckIcon, CalendarIcon } from 'lucide-react';
export function Hero() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };
  const floatingCardVariants = {
    hidden: {
      x: 50,
      opacity: 0,
      scale: 0.9
    },
    visible: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 80,
        damping: 12
      }
    }
  };
  return <section className="relative bg-white overflow-hidden pt-16 pb-24 lg:pt-24 lg:pb-32" data-id="element-103">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3" data-id="element-104">
        <div className="w-[600px] h-[600px] rounded-full bg-red-50 opacity-50 blur-3xl" data-id="element-105"></div>
      </div>
      <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3" data-id="element-106">
        <div className="w-[400px] h-[400px] rounded-full bg-gray-50 opacity-80 blur-3xl" data-id="element-107"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-108">
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 items-center" data-id="element-109">
          {/* Left Content */}
          <motion.div className="lg:col-span-6 text-center lg:text-left mb-16 lg:mb-0" variants={containerVariants} initial="hidden" animate="visible" data-id="element-110">
            <motion.div variants={itemVariants} className="mb-4" data-id="element-111">
              <span className="inline-block py-1 px-3 rounded-full bg-red-100 text-red-700 text-sm font-semibold tracking-wide" data-id="element-112">
                Marketing Experts. Driven by Results.
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight mb-6 leading-tight" data-id="element-113">
              Not Hitting Your Local{' '}
              <span className="text-red-600" data-id="element-114">SEO/PPC</span> Marketing Goals?
            </motion.h1>

            <motion.p variants={itemVariants} className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-115">
              We are Marketing Experts at Driving & Turning Traffic into
              Conversions with a Focus on Building Customer Loyalty & Retention!
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-116">
              <button className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-md font-bold text-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5" data-id="element-117">
                SCHEDULE FREE SESSION
              </button>
              <button className="bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 px-8 py-4 rounded-md font-bold text-lg transition-all shadow-sm hover:shadow-md" data-id="element-118">
                Learn More
              </button>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8 flex items-center justify-center lg:justify-start space-x-4 text-sm text-gray-500" data-id="element-119">
              <div className="flex -space-x-2" data-id="element-120">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center overflow-hidden" data-id="element-121">
                    <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User avatar" className="w-full h-full object-cover" data-id="element-122" />
                  </div>)}
              </div>
              <div className="flex flex-col" data-id="element-123">
                <div className="flex items-center text-yellow-400" data-id="element-124">
                  {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-125" />)}
                </div>
                <span data-id="element-126">Trusted by 500+ businesses</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Device Mockup & Floating Cards */}
          <div className="lg:col-span-6 relative h-[500px] sm:h-[600px] flex items-center justify-center" data-id="element-127">
            {/* Main Device Mockup (CSS only) */}
            <motion.div initial={{
            opacity: 0,
            y: 40
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="relative w-[280px] sm:w-[320px] h-[580px] sm:h-[640px] bg-gray-900 rounded-[3rem] border-[8px] border-gray-800 shadow-2xl overflow-hidden z-10" data-id="element-128">
              {/* Notch */}
              <div className="absolute top-0 inset-x-0 h-6 bg-gray-800 rounded-b-3xl w-40 mx-auto z-20" data-id="element-129"></div>

              {/* Screen Content */}
              <div className="absolute inset-0 bg-gray-50 pt-12 px-4 pb-6 flex flex-col" data-id="element-130">
                <div className="flex justify-between items-center mb-6" data-id="element-131">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center" data-id="element-132">
                    <span className="text-red-600 font-bold text-xs" data-id="element-133">ad</span>
                  </div>
                  <div className="w-24 h-2 bg-gray-200 rounded-full" data-id="element-134"></div>
                </div>

                <div className="space-y-4 flex-1" data-id="element-135">
                  <div className="w-full h-32 bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-id="element-136">
                    <div className="w-1/2 h-4 bg-gray-200 rounded mb-4" data-id="element-137"></div>
                    <div className="w-full h-2 bg-gray-100 rounded mb-2" data-id="element-138"></div>
                    <div className="w-3/4 h-2 bg-gray-100 rounded" data-id="element-139"></div>
                  </div>
                  <div className="w-full h-24 bg-red-50 rounded-xl border border-red-100 p-4" data-id="element-140">
                    <div className="w-1/3 h-4 bg-red-200 rounded mb-3" data-id="element-141"></div>
                    <div className="w-full h-8 bg-white rounded-md" data-id="element-142"></div>
                  </div>
                  <div className="w-full h-32 bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-id="element-143">
                    <div className="flex space-x-3 mb-4" data-id="element-144">
                      <div className="w-10 h-10 rounded-full bg-gray-200" data-id="element-145"></div>
                      <div className="flex-1" data-id="element-146">
                        <div className="w-1/2 h-3 bg-gray-200 rounded mb-2" data-id="element-147"></div>
                        <div className="w-1/3 h-2 bg-gray-100 rounded" data-id="element-148"></div>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded mb-2" data-id="element-149"></div>
                    <div className="w-5/6 h-2 bg-gray-100 rounded" data-id="element-150"></div>
                  </div>
                </div>

                {/* Bottom Nav */}
                <div className="h-14 bg-white rounded-2xl shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] flex justify-around items-center px-2 mt-auto" data-id="element-151">
                  <div className="w-8 h-8 rounded-full bg-red-100" data-id="element-152"></div>
                  <div className="w-8 h-8 rounded-full bg-gray-100" data-id="element-153"></div>
                  <div className="w-8 h-8 rounded-full bg-gray-100" data-id="element-154"></div>
                  <div className="w-8 h-8 rounded-full bg-gray-100" data-id="element-155"></div>
                </div>
              </div>
            </motion.div>

            {/* Floating Cards */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="absolute inset-0 z-20 pointer-events-none" data-id="element-156">
              {/* Card 1: New Lead */}
              <motion.div variants={floatingCardVariants} className="absolute top-[15%] -left-4 sm:-left-12 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-64 sm:w-72 rotate-[-2deg]" data-id="element-157">
                <div className="bg-green-100 p-2 rounded-lg flex-shrink-0" data-id="element-158">
                  <MessageSquareIcon className="w-5 h-5 text-green-600" data-id="element-159" />
                </div>
                <div data-id="element-160">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-161">
                    New Lead
                  </p>
                  <p className="text-sm font-semibold text-gray-800" data-id="element-162">
                    Sarah M.
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-163">
                    Requested a quote for SEO services
                  </p>
                </div>
              </motion.div>

              {/* Card 2: Review */}
              <motion.div variants={floatingCardVariants} className="absolute top-[40%] -right-8 sm:-right-16 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-60 sm:w-64 rotate-[3deg]" data-id="element-164">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0" data-id="element-165">
                  <StarIcon className="w-5 h-5 text-blue-600" data-id="element-166" />
                </div>
                <div data-id="element-167">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-168">
                    Google Review
                  </p>
                  <div className="flex text-yellow-400 mb-1" data-id="element-169">
                    {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-3 h-3 fill-current" data-id="element-170" />)}
                  </div>
                  <p className="text-xs text-gray-500 italic" data-id="element-171">
                    "Amazing results in just 2 months!"
                  </p>
                </div>
              </motion.div>

              {/* Card 3: Conversion */}
              <motion.div variants={floatingCardVariants} className="absolute bottom-[30%] -left-8 sm:-left-16 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-64 sm:w-72 rotate-[-1deg]" data-id="element-172">
                <div className="bg-red-100 p-2 rounded-lg flex-shrink-0" data-id="element-173">
                  <UserCheckIcon className="w-5 h-5 text-red-600" data-id="element-174" />
                </div>
                <div data-id="element-175">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-176">
                    New Conversion
                  </p>
                  <p className="text-sm font-semibold text-gray-800" data-id="element-177">
                    Form Submitted
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-178">
                    Campaign: Local PPC Q3
                  </p>
                </div>
              </motion.div>

              {/* Card 4: Appointment */}
              <motion.div variants={floatingCardVariants} className="absolute bottom-[10%] -right-4 sm:-right-12 bg-white p-3 sm:p-4 rounded-xl shadow-xl border border-gray-100 flex items-start space-x-3 w-56 sm:w-64 rotate-[2deg]" data-id="element-179">
                <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0" data-id="element-180">
                  <CalendarIcon className="w-5 h-5 text-purple-600" data-id="element-181" />
                </div>
                <div data-id="element-182">
                  <p className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-1" data-id="element-183">
                    Appointment
                  </p>
                  <p className="text-sm font-semibold text-gray-800" data-id="element-184">
                    Ashley C.
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-185">
                    Strategy Session @ 2PM
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}